import React from "react";
import { useParams } from "react-router";

import { PageLoader } from "@/components/PageLoader";
import { useTrackedQuery } from "@/hooks/useTrackedQuery";
import { EstimateTrackingData, getEstimateTrackingData } from "../queries/estimate-tracking-data";

export type EstimateTrackerRouteParams = "estimateNumber";

export function getEstimateTrackingDataQueryKey(estimateNumber: string) {
  return ["EstimateTracking", estimateNumber];
}

/**
 * Higher-Order Component to fetch the selected estimate tracking data for the current navigation route. A loading screen
 * is displayed while the tracking data is loading, then the data is passed to the wrapped component.
 */
export function withEstimateTrackingData<P extends object>(WrappedComponent: React.FC<EstimateTrackingData & P>) {
  return function WithEstimateTrackingData(props: P) {
    const estimateNumber = useParams<EstimateTrackerRouteParams>().estimateNumber!;
    const estimateQuery = useTrackedQuery<EstimateTrackingData>({
      queryKey: getEstimateTrackingDataQueryKey(estimateNumber),
      queryFn: async () => await getEstimateTrackingData(estimateNumber),
      retry: false,
    });

    if (estimateQuery.isError) return `Error loading estimate: ${estimateQuery.error.message}`;
    if (estimateQuery.isPending) return <PageLoader text="Loading estimate" />;

    const combinedProps = { ...estimateQuery.data, ...props };
    return <WrappedComponent {...combinedProps} />;
  };
}
